import React, {
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useMemo,
  useCallback,
} from "react";
import { Container, Graphics } from "@pixi/react";
import { useDispatch } from "react-redux";
import { symbolData, isCoinSymbol } from "../../SymbolData";
import { Settings } from "../../settings";
import { formatToCustomLocale } from "../../utilities";
import { FONT_STYLES, scaleStyle } from "../../fontStyles";
import * as PIXI from "pixi.js";
import { reportPotCollectionSymbolReady } from "../../State/actions";
import PotCollectionSymbolController from "./PotCollectionSymbolController";

// Match ReelControllerV2 constants exactly
const COIN_VALUE_Y_OFFSET = 82.67 / 338; // ≈ 0.2446 of symbolHeight

/**
 * PotCollectionSymbol
 *
 * Individual symbol cell for the Pot Collection feature.
 * Renders static textures + coin value text.
 *
 * Key differences from RoyalCollectSymbol:
 * - No locked royal concept
 * - No spin controller (PC doesn't have spinning reels — coins appear via ejection)
 * - No blurred symbols
 * - Uses GrekoNew PIXI.Text for coin values (not BitmapFont), centered
 */
const PotCollectionSymbol = forwardRef(
  (
    {
      symbolWidth,
      symbolHeight,
      index,
      reelIndex,
      symbolId,
      symbolsSpriteSheet,
    },
    ref,
  ) => {
    const symbolsContainerRef = useRef(null);
    const spinningContainerRef = useRef(null);
    const controllerRef = useRef(null);
    const maskRef = useRef(null);
    const staticSpriteRef = useRef(null);
    const valueTextRef = useRef(null);
    const reportedReadyRef = useRef(false);
    const currentSymbolIdRef = useRef(null);

    const dispatch = useDispatch();

    // Callback ref to track mask
    const setMaskRef = useCallback(
      (node) => {
        maskRef.current = node;
      },
      [],
    );

    // Create mask graphics draw function
    const drawMask = useCallback(
      (g) => {
        g.clear();
        g.beginFill(0x000000, 1);
        g.drawRect(
          -symbolWidth / 2,
          -symbolHeight / 2,
          symbolWidth,
          symbolHeight,
        );
        g.endFill();
      },
      [symbolWidth, symbolHeight],
    );

    const maskGraphics = useMemo(
      () => (
        <Graphics
          ref={setMaskRef}
          name={`pc_symbol_mask_${reelIndex}_${index}`}
          draw={drawMask}
        />
      ),
      [setMaskRef, reelIndex, index, drawMask],
    );

    const calculateScale = useCallback(
      (sid) => {
        if (!symbolHeight) return 1;
        // No padding in Pot Collection — all symbols render at natural size
        return symbolHeight / Settings.SYMBOL_HEIGHT;
      },
      [symbolHeight],
    );

    const getCoinDisplayValue = useCallback(
      (symbolValue) => {
        if (!symbolValue || symbolValue === null) return "";
        try {
          const coinValue = parseInt(symbolValue, 10);
          if (isNaN(coinValue) || coinValue <= 0) return "";

          // Values are already in subunits (pre-calculated by server or
          // normalized by RGS adapter) — just format, no bet multiplication
          return formatToCustomLocale(coinValue);
        } catch {
          return "";
        }
      },
      [],
    );

    /**
     * Set sprite texture + value text from symbolId.
     */
    const setSpriteFromSymbolId = useCallback(
      (container, targetSymbolId) => {
        if (!container || !symbolsSpriteSheet?.textures) return;

        if (targetSymbolId === null || targetSymbolId === undefined) {
          if (staticSpriteRef.current) {
            staticSpriteRef.current.texture = PIXI.Texture.EMPTY;
            staticSpriteRef.current.alpha = 0.001;
          }
          if (valueTextRef.current) {
            valueTextRef.current.text = "";
            valueTextRef.current.alpha = 0;
          }
          currentSymbolIdRef.current = null;
          return;
        }

        if (
          currentSymbolIdRef.current === targetSymbolId &&
          staticSpriteRef.current &&
          staticSpriteRef.current.texture !== PIXI.Texture.EMPTY
        ) {
          return;
        }

        const symbolInfo = symbolData[targetSymbolId];
        if (!symbolInfo) {
          if (staticSpriteRef.current) {
            staticSpriteRef.current.texture = PIXI.Texture.EMPTY;
            staticSpriteRef.current.alpha = 0.001;
          }
          currentSymbolIdRef.current = null;
          return;
        }

        const textureName = symbolInfo.texture;
        const symbolValue = symbolInfo.value;

        if (!textureName) {
          if (staticSpriteRef.current) {
            staticSpriteRef.current.texture = PIXI.Texture.EMPTY;
            staticSpriteRef.current.alpha = 0.001;
          }
          if (valueTextRef.current) {
            valueTextRef.current.text = "";
            valueTextRef.current.alpha = 0;
          }
          currentSymbolIdRef.current = null;
          return;
        }

        const texture =
          symbolsSpriteSheet.textures[`${textureName}.png`] || null;

        if (!texture) return;

        if (!staticSpriteRef.current) {
          const sprite = new PIXI.Sprite(texture);
          sprite.anchor.set(0.5);
          sprite.name = "pc_symbol_texture";
          sprite.roundPixels = true;
          container.addChild(sprite);
          staticSpriteRef.current = sprite;
        } else {
          staticSpriteRef.current.texture = texture;
        }

        const scale = calculateScale(targetSymbolId);
        staticSpriteRef.current.scale.set(scale);

        currentSymbolIdRef.current = targetSymbolId;

        // Coin value text (PIXI.Text with GrekoNew, not BitmapText)
        if (symbolValue && isCoinSymbol(targetSymbolId)) {
          if (!valueTextRef.current) {
            const fontSize = symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
            const baseStyle = FONT_STYLES.winText;
            const textStyle = new PIXI.TextStyle(scaleStyle(baseStyle, fontSize));

            const vt = new PIXI.Text("", textStyle);
            vt.anchor.set(0.5);
            vt.name = "pc_symbol_value";
            vt.zIndex = 1;
            vt.roundPixels = true;
            container.addChild(vt);
            valueTextRef.current = vt;
          }

          if (valueTextRef.current) {
            const displayValue = getCoinDisplayValue(symbolValue);
            valueTextRef.current.text = displayValue;
            valueTextRef.current.alpha = displayValue !== "" ? 1 : 0;
            valueTextRef.current.y = symbolHeight * COIN_VALUE_Y_OFFSET;

            // Auto-fit scaling
            if (displayValue !== "") {
              valueTextRef.current.scale.set(1);

              const coinWidth = symbolWidth;
              const paddingPercent = 0.12;
              const maxTextWidth = coinWidth * (1 - paddingPercent * 2);
              const actualTextWidth = valueTextRef.current.width;

              if (actualTextWidth > maxTextWidth) {
                const fitScale = maxTextWidth / actualTextWidth;
                valueTextRef.current.scale.set(fitScale);
              }
            }
          }
        } else if (valueTextRef.current) {
          valueTextRef.current.text = "";
          valueTextRef.current.alpha = 0;
        }
      },
      [symbolsSpriteSheet, symbolWidth, symbolHeight, calculateScale, getCoinDisplayValue],
    );

    // Phase 1: Create static sprite
    useEffect(() => {
      if (!symbolsContainerRef.current || !symbolsSpriteSheet?.textures) return;

      const container = symbolsContainerRef.current;
      while (container.children.length > 0) {
        container.removeChildAt(0);
      }
      staticSpriteRef.current = null;
      valueTextRef.current = null;

      setSpriteFromSymbolId(container, symbolId);

      if (!reportedReadyRef.current) {
        dispatch(reportPotCollectionSymbolReady());
        reportedReadyRef.current = true;
      }

      return () => {
        staticSpriteRef.current = null;
        valueTextRef.current = null;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Phase 2: Create spin controller (after static sprite is ready)
    useEffect(() => {
      if (!symbolsContainerRef.current || !symbolsSpriteSheet?.textures) return;

      // Create a spinning container as a sibling of the static sprites container.
      // It shares the same parent (and thus the same mask) but is layered behind the static sprite.
      const parentContainer = symbolsContainerRef.current.parent;
      if (!parentContainer) return;

      // Only create once
      if (spinningContainerRef.current) return;

      const spinCont = new PIXI.Container();
      spinCont.name = `pc_spinning_${reelIndex}_${index}`;
      spinCont.roundPixels = true;
      // Offset so sprite[VISIBLE_SYMBOL_INDEX] (index 2) sits at y=0 (mask center).
      // Sprites are at y = 0, h, 2h, 3h inside spinCont.
      // Shifting spinCont.y by -2h maps sprite[2] to y=0, within the mask clip area.
      spinCont.y = -2 * symbolHeight;
      // Add BEFORE the static sprites container so spinning sprites render behind
      const staticIndex = parentContainer.getChildIndex(symbolsContainerRef.current);
      parentContainer.addChildAt(spinCont, staticIndex);
      spinningContainerRef.current = spinCont;

      // Create the controller
      controllerRef.current = new PotCollectionSymbolController(
        spinCont,
        symbolId,
        symbolWidth,
        symbolHeight,
        index,
        reelIndex,
        null, // onStopCallback — set dynamically per spin by the engine
        symbolsSpriteSheet,
      );

      return () => {
        if (controllerRef.current) {
          controllerRef.current.cleanup();
          controllerRef.current = null;
        }
        if (spinningContainerRef.current) {
          if (spinningContainerRef.current.parent) {
            spinningContainerRef.current.parent.removeChild(spinningContainerRef.current);
          }
          spinningContainerRef.current.destroy({ children: true });
          spinningContainerRef.current = null;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [symbolsSpriteSheet]);

    // Resize handler
    useEffect(() => {
      if (staticSpriteRef.current && staticSpriteRef.current.texture !== PIXI.Texture.EMPTY) {
        const scale = calculateScale(currentSymbolIdRef.current);
        staticSpriteRef.current.scale.set(scale);
      }
      if (valueTextRef.current && valueTextRef.current.text !== "") {
        // Recalculate fontSize to match new symbolHeight
        const newFontSize = symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
        const baseStyle = FONT_STYLES.winText;
        Object.assign(valueTextRef.current.style, scaleStyle(baseStyle, newFontSize));

        valueTextRef.current.y = symbolHeight * COIN_VALUE_Y_OFFSET;
        valueTextRef.current.scale.set(1);
        const maxTextWidth = symbolWidth * (1 - 0.12 * 2);
        const actualTextWidth = valueTextRef.current.width;
        if (actualTextWidth > maxTextWidth) {
          valueTextRef.current.scale.set(maxTextWidth / actualTextWidth);
        }
      }
      // Resize the spin controller's sprites too
      if (controllerRef.current) {
        controllerRef.current.resize(symbolWidth, symbolHeight);
      }
      // Keep spinning container offset in sync — but only when controller applied
      // immediately. When deferred (_pendingPositionUpdate=true), the controller's
      // _applyPendingResize() handles the offset to keep sprites and container in sync.
      if (spinningContainerRef.current && !controllerRef.current?._pendingPositionUpdate) {
        spinningContainerRef.current.y = -2 * symbolHeight;
      }
    }, [symbolWidth, symbolHeight, calculateScale]);

    useImperativeHandle(
      ref,
      () => ({
        hideSymbolTexture: () => {
          if (staticSpriteRef.current) {
            staticSpriteRef.current.alpha = 0.001;
          }
          if (valueTextRef.current) {
            valueTextRef.current.alpha = 0;
          }
        },
        showSymbolTexture: () => {
          if (staticSpriteRef.current && staticSpriteRef.current.texture !== PIXI.Texture.EMPTY) {
            staticSpriteRef.current.alpha = 1;
          }
          if (valueTextRef.current && valueTextRef.current.text !== "") {
            valueTextRef.current.alpha = 1;
          }
        },
        updateSymbolTexture: (symId) => {
          setSpriteFromSymbolId(symbolsContainerRef.current, symId);
        },
        // ── Spin API (used by PotCollectionSpinEngine) ──
        getSymbolController: () => {
          if (!controllerRef.current) {
          }
          return controllerRef.current;
        },
        isControllerReady: () => !!controllerRef.current,
        startSpin: () => {
          const controller = controllerRef.current;
          if (!controller) {
            return;
          }
          // Hide static sprite — spinning sprites take over
          if (staticSpriteRef.current) staticSpriteRef.current.alpha = 0.001;
          if (valueTextRef.current) valueTextRef.current.alpha = 0;
          controller.startSpin();
        },
        startWindUp: (onComplete) => {
          const controller = controllerRef.current;
          if (!controller) {
            if (onComplete) onComplete();
            return Promise.resolve();
          }
          // Forward wind-up completion callback to controller
          controller.onWindUpComplete = onComplete || null;
          // Hide static sprite — spinning sprites take over
          if (staticSpriteRef.current) staticSpriteRef.current.alpha = 0.001;
          if (valueTextRef.current) valueTextRef.current.alpha = 0;
          return controller.startSpinWithWindUp();
        },
        stopSpin: (isManualStop) => {
          controllerRef.current?.stopSpin(isManualStop);
        },
        stopSpinImmediate: () => {
          controllerRef.current?.stopSpinImmediate();
        },
        startSpinNoWindUp: () => {
          const controller = controllerRef.current;
          if (!controller) return;
          // Hide static sprite — spinning sprites take over
          if (staticSpriteRef.current) staticSpriteRef.current.alpha = 0.001;
          if (valueTextRef.current) valueTextRef.current.alpha = 0;
          controller.startSpinNoWindUp();
        },
        setLandingSymbolId: (id) => {
          controllerRef.current?.setLandingSymbolId(id);
        },
        // ── Tension API (used by PotCollectionSpinEngine) ──
        spinTension: () => {
          controllerRef.current?.spinTension();
        },
        stopSpinTension: () => {
          controllerRef.current?.stopSpinTension();
        },
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    const containerY = useMemo(
      () => symbolHeight * index,
      [symbolHeight, index],
    );

    return (
      <Container y={containerY} name={`pc_symbol_outer_${reelIndex}_${index}`}>
        {maskGraphics}
        <Container
          name={`pc_symbol_content_${reelIndex}_${index}`}
          mask={maskRef.current}
        >
          <Container
            ref={symbolsContainerRef}
            name={`pc_symbol_sprites_${reelIndex}_${index}`}
            sortableChildren
          />
        </Container>
      </Container>
    );
  },
);

PotCollectionSymbol.displayName = "PotCollectionSymbol";

export default React.memo(PotCollectionSymbol);
